import { useCallback } from 'react';
import P, { InferProps } from 'prop-types';
import { ROLES, FIELD_TYPES } from '../../../types';
import SwitchField from './switch-field';
import TextInput from './text-field';
import SelectField from './select-field';
import ImageField from './image-field';
import ColorField from './color-field';
import InfoFields from './info-fields';
import DateField from './date-field';
import DateTimeField from './datetime-field';
import PushNotificationImage from './push-image-field';
import PromoField from './promo-field';
import PromoSelectField from './promo-select-field';
import PromoSelectFieldMultiple from './promo-select-field-multiple';
import MultipleSelectField from './multiple-select-field';
import ValidateRSSField from './validates-rss-field';
import PromoFieldTimed from './promo-field-timed';
import { useUserAuth } from '../../../contexts/auth';
import RichTextField from './rich-text-field';
import RichTextHtmlField from './rich-text-html-field';

function RenderModuleField({
  field,
  setChange,
  setValidation,
  path,
  imagePath,
  keyPath,
  setPromosInUse,
  fullWidth,
}: InferProps<typeof RenderModuleField.propTypes>) {
  const { isSuperAdmin, teamPermissions } = useUserAuth();

  const isReadOnlyAndNotSuperadmin = () => {
    if (path && !isSuperAdmin) {
      return teamPermissions[path] === ROLES.VIEWER;
    } else {
      return false;
    }
  };

  const setChangeWithPath = useCallback(
    (key: string, value: string, validation: any, isTeamDataValue: boolean) => {
      if (setChange && keyPath) {
        setChange(`${keyPath}.value`, value, validation, isTeamDataValue);
      } else if (setChange) {
        setChange(key, value, validation, isTeamDataValue);
      }
    },
    [setChange, keyPath]
  );

  switch (field.type) {
    case FIELD_TYPES.TEXT:
      return (
        <TextInput
          setChange={setChangeWithPath}
          setValidation={setValidation}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          fullWidth={fullWidth}
          numRows={field.numRows}
        />
      );
    case FIELD_TYPES.RICH_TEXT:
      return (
        <RichTextField
          setChange={setChangeWithPath}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
        />
      );
    case FIELD_TYPES.RICH_TEXT_HTML:
      return (
        <RichTextHtmlField
          setChange={setChangeWithPath}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
        />
      );
    case FIELD_TYPES.NUMBER:
      return (
        <TextInput
          setChange={setChangeWithPath}
          setValidation={setValidation}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          fullWidth={fullWidth}
        />
      );
    case FIELD_TYPES.URL:
      return (
        <TextInput
          setChange={setChangeWithPath}
          setValidation={setValidation}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          fullWidth={fullWidth}
        />
      );
    case FIELD_TYPES.SWITCH:
      return (
        <SwitchField
          setChange={setChangeWithPath}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
        />
      );
    case FIELD_TYPES.SELECT:
      return (
        <SelectField
          setChange={setChangeWithPath}
          setValidation={setValidation}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          fullWidth={fullWidth}
        />
      );
    case FIELD_TYPES.MULTI_SELECT:
      return (
        <MultipleSelectField
          setChange={setChangeWithPath}
          setValidation={setValidation}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          fullWidth={fullWidth}
        />
      );
    case FIELD_TYPES.IMAGE:
      return (
        <ImageField
          setChange={setChangeWithPath}
          field={field}
          path={path}
          imagePath={imagePath}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
        />
      );
    case FIELD_TYPES.PUSH_NOTIFICATION_IMAGE:
      return (
        <PushNotificationImage
          setChange={setChangeWithPath}
          field={field}
          path={path}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
        />
      );
    case FIELD_TYPES.COLOR_PICKER:
      return (
        <ColorField
          setChange={setChangeWithPath}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
        />
      );
    case FIELD_TYPES.INFO_FIELDS:
      return (
        <InfoFields
          setChange={setChangeWithPath}
          setValidation={setValidation}
          fields={field.fields}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
        />
      );
    case FIELD_TYPES.PROMO_FIELD:
      return (
        <PromoField
          setChange={setChangeWithPath}
          setValidation={setValidation}
          title={field.id.replace('-', ' ')}
          fields={field.fields}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          path={path}
          setPromosInUse={setPromosInUse}
        />
      );
    case FIELD_TYPES.PROMO_SELECT:
      return (
        <PromoSelectField
          setChange={setChangeWithPath}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          path={path}
        />
      );
    case FIELD_TYPES.PROMO_SELECT_MULTIPLE:
      return (
        <PromoSelectFieldMultiple
          setChange={setChangeWithPath}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          path={path}
        />
      );
    case FIELD_TYPES.PROMO_TIMED:
      return (
        <PromoFieldTimed
          setChange={setChangeWithPath}
          setValidation={setValidation}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          path={path}
        />
      );
    case FIELD_TYPES.DATE:
      return (
        <DateField
          setChange={setChangeWithPath}
          setValidation={setValidation}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
          fullWidth={fullWidth}
        />
      );
    case FIELD_TYPES.DATETIME:
      return (
        <DateTimeField
          setChange={setChangeWithPath}
          setValidation={setValidation}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
        />
      );
    case FIELD_TYPES.VALIDATE_RSS:
      return (
        <ValidateRSSField
          setChange={setChangeWithPath}
          setValidation={setValidation}
          field={field}
          key={field.id}
          isReadOnly={isReadOnlyAndNotSuperadmin() || field.isReadOnly}
        />
      );
    default:
      console.error('Field is missing component! Type:', field.type);
      return null;
  }
}

RenderModuleField.propTypes = {
  field: P.any.isRequired,
  setChange: P.func.isRequired,
  setValidation: P.func.isRequired,
  path: P.string.isRequired,
  imagePath: P.string,
  keyPath: P.string,
  setPromosInUse: P.func,
  fullWidth: P.bool,
};

export default RenderModuleField;
